import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ProfessionalRaffleScreen } from '../features/raffle/professional/ui/professional-raffle-screen';
import { getEmployeeReferralsCount } from '../shared/api/employee.service';
import { useEmployeeReferralLinkActions } from '../shared/hooks/use-employee-referral-link-actions';
import { useEmployeeSession } from '../shared/hooks/use-employee-session';
import { ViewportFrame, ViewportPage } from '../shared/ui';

const FEEDBACK_DURATION_MS = 2200;

export function ReferralRaffleProfessionalPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status } = useEmployeeSession();
  const { shareReferralLink } = useEmployeeReferralLinkActions();
  const feedbackTimerRef = useRef<number | null>(null);
  const [actionSupportText, setActionSupportText] = useState<string | null>(
    null,
  );
  const referralsCountQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeReferralsCount,
    queryKey: ['employee', 'referrals', 'count'],
  });

  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current !== null) {
        window.clearTimeout(feedbackTimerRef.current);
      }
    };
  }, []);

  const showFeedback = (text: string) => {
    setActionSupportText(text);

    if (feedbackTimerRef.current !== null) {
      window.clearTimeout(feedbackTimerRef.current);
    }

    feedbackTimerRef.current = window.setTimeout(() => {
      setActionSupportText(null);
      feedbackTimerRef.current = null;
    }, FEEDBACK_DURATION_MS);
  };

  const handlePrimaryAction = async () => {
    const result = await shareReferralLink();

    if (result === 'copied') {
      showFeedback(t('referralBonus.copySuccessToast'));
    }
  };

  const referralsCount = referralsCountQuery.data;

  return (
    <ViewportPage dataUi="referral-raffle-professional-page">
      <ViewportFrame dataUi="referral-raffle-professional-page-frame">
        <ProfessionalRaffleScreen
          backAriaLabel={t('referralBonus.backAria')}
          chancePercent={referralsCount?.chancePercent ?? 0}
          onBack={() => {
            navigate('/referral');
          }}
          onPrimaryAction={() => {
            void handlePrimaryAction();
          }}
          primaryActionSupportText={actionSupportText}
          referralsCount={referralsCount?.total ?? 0}
          ticketsCount={referralsCount?.ticketsCount ?? 0}
        />
      </ViewportFrame>
    </ViewportPage>
  );
}
